import toast from 'react-hot-toast';
import Request from '@/services/util/request-service';
import { CourseData, UnitId, SubunitId } from 'course-types';

/**
 * Service for handling course content updates (names, descriptions, etc.)
 * Centralizes API calls and Redux state update patterns.
 */
const CourseUpdateService = {
  /**
   * Update course name
   */
  async updateCourseName(
    courseId: string,
    name: string,
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const requestResponse = await Request.post('course/update', {
        courseId,
        name: name.trim(),
      });

      const response = await requestResponse?.json();

      if (!response || response.status !== 'success') {
        const errorMessage = response?.data || 'Unknown error';
        console.error('API Error:', errorMessage);
        toast.error(`Error updating course name: ${errorMessage}`);
        return { success: false, error: errorMessage };
      }

      toast.success('Course name updated');
      return { success: true };
    } catch (error) {
      console.error('Error updating course name:', error);
      toast.error('Failed to update course name');
      return { success: false, error: String(error) };
    }
  },

  /**
   * Update unit name and/or description
   */
  async updateUnit(
    unitId: string,
    name: string,
    description: string,
  ): Promise<{ success: boolean; error?: string }> {
    try {
      console.log('Sending unit update request:', { unitId, name, description });

      const requestResponse = await Request.post('course/update/unit', {
        unitId,
        name: name.trim(),
        description,
      });

      const response = await requestResponse?.json();
      console.log('Parsed response:', response);

      if (!response || response.status !== 'success') {
        const errorMessage = response?.data || 'Unknown error';
        console.error('API Error:', errorMessage);
        toast.error(`Error updating unit name: ${errorMessage}`);
        return { success: false, error: errorMessage };
      }

      console.log('Unit update successful');
      toast.success('Unit name updated');
      return { success: true };
    } catch (error) {
      console.error('Error updating unit name:', error);
      toast.error('Failed to update unit name');
      return { success: false, error: String(error) };
    }
  },

  /**
   * Update subunit name
   */
  async updateSubunit(
    subunitId: string,
    name: string,
  ): Promise<{ success: boolean; error?: string }> {
    try {
      console.log('Sending subunit update request:', { subunitId, name });

      const requestResponse = await Request.post('course/update/subunit', {
        subunitId,
        name: name.trim(),
      });

      const response = await requestResponse?.json();
      console.log('Parsed response:', response);

      if (!response || response.status !== 'success') {
        const errorMessage = response?.data || 'Unknown error';
        console.error('API Error:', errorMessage);
        toast.error(`Error updating subunit name: ${errorMessage}`);
        return { success: false, error: errorMessage };
      }

      console.log('Subunit update successful');
      toast.success('Subunit name updated');
      return { success: true };
    } catch (error) {
      console.error('Error updating subunit name:', error);
      toast.error('Failed to update subunit name');
      return { success: false, error: String(error) };
    }
  },

  /**
   * Build updated course data with new unit name
   */
  buildUpdatedCourseWithUnitName(
    activeCourse: CourseData,
    unitId: string,
    name: string,
  ): CourseData {
    return {
      ...activeCourse,
      units: {
        ...activeCourse.units,
        [unitId as UnitId]: {
          ...activeCourse.units[unitId as UnitId],
          name: name.trim(),
        },
      },
    };
  },

  /**
   * Build updated course data with new subunit name
   */
  buildUpdatedCourseWithSubunitName(
    activeCourse: CourseData,
    unitId: string,
    subunitId: string,
    name: string,
  ): CourseData {
    return {
      ...activeCourse,
      units: {
        ...activeCourse.units,
        [unitId as UnitId]: {
          ...activeCourse.units[unitId as UnitId],
          subunits: {
            ...activeCourse.units[unitId as UnitId]?.subunits,
            [subunitId as SubunitId]: {
              ...activeCourse.units[unitId as UnitId]?.subunits[subunitId as SubunitId],
              name: name.trim(),
            },
          },
        },
      },
    };
  },
};

export default CourseUpdateService;
